import { FC, lazy, Suspense, useEffect, useLayoutEffect, useMemo } from 'react';
import { useQueries } from 'react-query';
import { JsonLd } from 'react-schemaorg';
import { WebSite } from 'schema-dts';

import { BannerData } from '@/components/elements/Banner';
import Helmet from '@/components/meta/Helmet';
import { useSetBreadcrumbContext } from '@/contexts/breadcrumb';
import { useLayoutContext } from '@/contexts/layout';
import { useUserContext } from '@/contexts/user';
import {
  Announcement,
  MultiraceProgram,
  OrderDateLargeDividend,
  useGetAnnouncementQuery,
  useGetMultiraceProgramsQuery,
  useGetOrderDateLargeDividendQuery,
  useGetRecentRaceProgramsQuery,
} from '@/lib/apollo/types/gen/graphql';
import { dayjst } from '@/lib/dayjst';
import { getLink } from '@/lib/mainvisual';
import { client } from '@/lib/microcms';
import { Banner } from '@/lib/microcms/model';
import { mq } from '@/utility/mediaQuery';

import { NewsType } from '../News/types';
import { RecentRacePrograms } from './types';

const TopGuestTemplate = lazy(() => import('@/components/templates/Top/Guest'));
const TopTemplate = lazy(() => import('@/components/templates/Top'));

export type NewsMoreFlags = {
  news: boolean;
  blog: boolean;
};

export type Banners = {
  main: Banner[];
  top: BannerData[];
  middle: BannerData[];
  bottom: BannerData[];
};

const NEWS_LIMIT = 3;
const BLOG_LIMIT = 3;

const getBannerList = (banners: Banner[]): BannerData[] =>
  banners
    .filter(item => (item.image ? true : false)) // 「image」が存在する場合にのみ「.map」の処理を実行する
    .map(banner => {
      return {
        srcSet: `${banner.image.url}?width=240&height=75&dpr=1 1x, ${banner.image.url}?width=240&height=75&dpr=2 2x`,
        mediaSet: [
          {
            srcSet: `${banner.image.url}?width=240&height=75&dpr=1&fm=webp 1x, ${banner.image.url}?width=240&height=75&dpr=2&fm=webp 2x`,
            type: 'image/webp',
          },
        ],
        alt: banner.alt,
        link: getLink(banner),
        external: banner.linkPath
          ? !(
              banner.linkPath.startsWith(import.meta.env.BASE_URL) ||
              banner.linkPath.startsWith('/')
            )
          : false,
      };
    });

const Top: FC = () => {
  const { setLayoutStyles } = useLayoutContext();
  const setBreadcrumbs = useSetBreadcrumbContext();

  useLayoutEffect(() => {
    setLayoutStyles({
      customStyles: mq({
        maxWidth: '100%',
        margin: ['0 auto 40px', '0 auto 40px', '0 auto 40px', '0 auto 40px'],
      }),
    });
  }, []);
  const { isGuest } = useUserContext();

  const date = useMemo(() => dayjst(), []);

  const raceProgramsQuery = useGetRecentRaceProgramsQuery({
    variables: {
      prev: {
        heldDate: date.subtract(1, 'day'),
      },
      today: {
        heldDate: date,
      },
      next: {
        heldDate: date.add(1, 'day'),
      },
    },
  });
  const multiraceProgramsQuery = useGetMultiraceProgramsQuery({
    variables: {
      input: {
        heldDate: date,
      },
    },
  });
  const announcementQuery = useGetAnnouncementQuery();

  const largeDividend = useGetOrderDateLargeDividendQuery({
    variables: { period: 10 },
  });

  const nowISO = dayjst().toISOString();
  const dateString = dayjst().format('YYYY-MM-DD');

  const [
    { data: bannerData },
    { data: newsData },
    { data: blogData },
    { data: campaignData },
  ] = useQueries([
    {
      queryKey: ['bannerdata', dateString],
      queryFn: async () => client.getObjectContent('bannerdata'),
    },
    {
      queryKey: ['news_top', dateString],
      queryFn: async () =>
        client.getContentsRaw(NewsType.News, {
          limit: NEWS_LIMIT,
          fields: 'id,title,publishedAt,updatedAt',
          orders:
            import.meta.env.NODE_ENV === 'production'
              ? '-publishedAt'
              : undefined,
        }),
    },
    {
      queryKey: ['blog_top', dateString],
      queryFn: async () =>
        client.getContentsRaw(NewsType.Blog, {
          limit: BLOG_LIMIT,
          fields: 'id,title,featuredImage,publishedAt,updatedAt',
          orders:
            import.meta.env.NODE_ENV === 'production'
              ? '-publishedAt'
              : undefined,
        }),
    },
    {
      queryKey: ['current_campaign_top', dateString],
      queryFn: async () =>
        client.getContents(NewsType.Campaign, {
          limit: 100,
          fields: 'id,raceProgramInfo',
          filters: `endTime[greater_than]${nowISO}[and]startTime[less_than]${nowISO}`,
        }),
    },
  ]);

  useEffect(() => {
    setBreadcrumbs([]);
  }, []);

  return (
    <>
      <Helmet
        meta={{
          description:
            'インターネットで簡単に全国の競輪場へ投票できる。毎月お得なキャンペーン実施中。レーススケジュールに加え選手メモや収支管理など投票に役立つ機能も充実。DMM競輪で競輪投票をお楽しみください。',
        }}
      />
      <JsonLd<WebSite>
        item={{
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          name: 'DMM競輪',
          url: 'https://keirin.dmm.com',
          image: `https://keirin.dmm.com/images/ogp/default.png`,
          inLanguage: 'ja',
          sameAs: [
            'https://twitter.com/DMM_Chariloto',
            'https://www.youtube.com/channel/UCmiXL_z9owqynl0pFQ8XjTA',
          ],
        }}
      />
      <Suspense fallback={<></>}>
        {isGuest ? (
          <TopGuestTemplate
            recentRacePrograms={raceProgramsQuery.data as RecentRacePrograms}
            multiracePrograms={
              multiraceProgramsQuery.data === undefined
                ? []
                : (multiraceProgramsQuery.data
                    .multiracePrograms as MultiraceProgram[])
            }
            banners={{
              main: [],
              top: getBannerList(
                bannerData ? [...bannerData.main, ...bannerData.top] : [],
              ),
              middle: getBannerList(bannerData ? bannerData.middle : []),
              bottom: getBannerList(bannerData ? bannerData?.bottom : []),
            }}
            news={newsData?.contents || []}
            blogs={blogData?.contents || []}
            campaigns={campaignData || []}
          />
        ) : (
          <TopTemplate
            recentRacePrograms={raceProgramsQuery.data as RecentRacePrograms}
            multiracePrograms={
              multiraceProgramsQuery.data === undefined
                ? []
                : (multiraceProgramsQuery.data
                    .multiracePrograms as MultiraceProgram[])
            }
            announcement={announcementQuery.data?.announcement as Announcement}
            banners={{
              main: bannerData ? bannerData.main : [],
              top: getBannerList(bannerData ? bannerData.top : []),
              middle: getBannerList(bannerData ? bannerData.middle : []),
              bottom: getBannerList(bannerData ? bannerData?.bottom : []),
            }}
            news={newsData?.contents || []}
            blogs={blogData?.contents || []}
            campaigns={campaignData || []}
            largeDividend={
              largeDividend.data
                ?.orderDateLargeDividend as OrderDateLargeDividend
            }
          />
        )}
      </Suspense>
    </>
  );
};

export default Top;
